import { zodResolver } from "@hookform/resolvers/zod";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { Control, useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import { useControlsQuery } from "org/control/queries/useControlsQuery";
import { useTestingProgramQuery } from "org/controlTesting/testingProgram/view/queries/useTestingProgramQuery";
import {
    BULK_CREATE_MODE,
    TESTING_CONTROL_CREATE_MODE_PARAM,
    TESTING_PROGRAM_ID_QUERY_PARAM,
    buildTestingProgramTestingControlsListUrl,
    testingControlCreateModeParamSchema,
    testingProgramIdParamSchema,
} from "org/controlTesting/urlUtils";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { useAvailableActions } from "../../../../utils/hooks/useAvailableActions";
import { useUserGroupsQuery } from "../../../user/queries/useUserGroupsQuery";

import {
    getTestingControlFormDefaultValues,
    useTestingControlViewAnchors,
} from "./consts";
import { useCreateBulkTestingControlMutation } from "./queries/useCreateBulkTestingControlMutation";
import { useCreateTestingControlMutation } from "./queries/useCreateTestingControlMutation";
import { TestingControlForm } from "./TestingControlForm";
import {
    TestingControlCreateSchema,
    TestingControlFormSchema,
    TestingControlBulkCreateSchema,
    getTestingControlSchema,
} from "./testingControlSchema";
import {
    formatBulkCreatedTestsToNumber,
    toBulkCreatableTestingControlBody,
    toCreatableTestingControlBody,
} from "./utils";

export function TestingControlCreatePage() {
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const unparsedTestingProgramId = searchParams.get(
        TESTING_PROGRAM_ID_QUERY_PARAM
    );
    const unparsedModeParam = searchParams.get(
        TESTING_CONTROL_CREATE_MODE_PARAM
    );
    const testingProgramIdParseResult = testingProgramIdParamSchema.safeParse(
        unparsedTestingProgramId
    );
    const modeParseResult =
        testingControlCreateModeParamSchema.safeParse(unparsedModeParam);

    const canCreate = useAvailableActions().testingControl.create;
    if (!canCreate) {
        return <LoggedInHTTPError code={403} />;
    }

    if (testingProgramIdParseResult.success === false) {
        console.error(
            `Invalid testing program id: ${unparsedTestingProgramId}`
        );
        return <LoggedInHTTPError code={400} />;
    }
    if (modeParseResult.success === false) {
        console.error(
            `Invalid testing control create mode: ${unparsedModeParam}.`
        );
    }
    const testingProgramId = testingProgramIdParseResult.data;

    return (
        <View
            testingProgramId={testingProgramId}
            mode={modeParseResult.data ?? undefined}
        />
    );
}

interface ViewProps {
    testingProgramId: number;
    mode: typeof BULK_CREATE_MODE | undefined;
}
function View({ testingProgramId, mode }: ViewProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutateAsync, isPending: isSinglePending } =
        useCreateTestingControlMutation();
    const { data: controlsData, status: controlsQueryStatus } =
        useControlsQuery();
    const { mutateAsync: mutateBulkAsync, isPending: isBulkPending } =
        useCreateBulkTestingControlMutation();
    const isPending = isBulkPending || isSinglePending;
    const timezoneName =
        useCurrentUserTimezone() as TestingControlFormSchema["timezoneName"];
    const isBulkCreateMode = mode === BULK_CREATE_MODE;

    const schema = getTestingControlSchema(
        isBulkCreateMode ? "BULK_CREATE" : "CREATE",
        t
    );
    const { formState, handleSubmit, control, trigger, setValue, subscribe } =
        useForm<TestingControlFormSchema>({
            mode: "onBlur",
            resolver: zodResolver(schema),
            defaultValues: getTestingControlFormDefaultValues(
                timezoneName,
                isBulkCreateMode ? "BULK_CREATE" : "CREATE"
            ),
        });

    const handleSubmitClick = handleSubmit(
        async (data: TestingControlFormSchema) => {
            if (isBulkCreateMode) {
                if (controlsQueryStatus !== "success") {
                    throw new Error(
                        "Expected controls query to be success before bulk submission"
                    );
                }
                const body = await toBulkCreatableTestingControlBody(
                    data as TestingControlBulkCreateSchema,
                    testingProgramId,
                    t,
                    controlsData
                );
                const res = await mutateBulkAsync({
                    data: body,
                });
                if (res.status === 200) {
                    navigate(
                        buildTestingProgramTestingControlsListUrl(
                            testingProgramId
                        )
                    );
                }
                return;
            }
            const creatableTestingControlBody = toCreatableTestingControlBody(
                data as TestingControlCreateSchema,
                testingProgramId,
                t
            );
            const res = await mutateAsync({
                data: creatableTestingControlBody,
            });
            if (res.status === 201) {
                navigate(
                    buildTestingProgramTestingControlsListUrl(testingProgramId)
                );
            }
        },
        (error) => {
            console.error(error);
            emitNotification(
                "error",
                t("TestingControlView_SubmitValidationError")
            );
        }
    );

    const anchors = useTestingControlViewAnchors(
        isBulkCreateMode ? "BULK_CREATE" : "CREATE"
    );
    const { data: testingProgramData } =
        useTestingProgramQuery(testingProgramId);

    return (
        <OrgApplicationBar
            title={{
                title: isBulkCreateMode
                    ? t("ControlTesting_TestingControlCreateBulkTitle")
                    : t("ControlTesting_TestingControlCreateTitle"),
                breadCrumbs: [
                    {
                        label: testingProgramData.content.resource.name,
                        url: buildTestingProgramTestingControlsListUrl(
                            testingProgramId
                        ),
                    },
                ],
            }}
            navigation={{
                type: "Anchors",
                anchors,
            }}
        >
            <FormFooterWrapper>
                <TestingControlForm
                    disabled={false}
                    control={control}
                    trigger={trigger}
                    setValue={setValue}
                    subscribe={subscribe}
                    pageType={isBulkCreateMode ? "BULK_CREATE" : "CREATE"}
                />
                <StickyFooter>
                    <SubmitButton
                        disabled={
                            !formState.isDirty ||
                            (isBulkCreateMode &&
                                controlsQueryStatus !== "success")
                        }
                        loading={isPending}
                        onClick={() => void handleSubmitClick()}
                        control={control}
                        isBulkCreateMode={isBulkCreateMode}
                    />
                    <ViewCancelButton
                        isDirty={formState.isDirty}
                        bodyLabel={t("TestingControlView_ConfirmCancel_Body")}
                    />
                </StickyFooter>
            </FormFooterWrapper>
        </OrgApplicationBar>
    );
}

interface SubmitButtonProps {
    control: Control<TestingControlFormSchema>;
    isBulkCreateMode: boolean;
    onClick: () => void;
    loading: boolean;
    disabled: boolean;
}
function SubmitButton({
    control,
    isBulkCreateMode,
    onClick,
    loading,
    disabled,
}: SubmitButtonProps) {
    const { t } = useTranslation();
    if (!isBulkCreateMode) {
        return (
            <Button
                size="large"
                variant="primary"
                disabled={disabled}
                loading={loading}
                onClick={onClick}
            >
                {t("TestingControlView_CreateButton")}
            </Button>
        );
    }
    return (
        <BulkSubmitButton
            control={control}
            onClick={onClick}
            loading={loading}
            disabled={disabled}
        />
    );
}

interface BulkSubmitButtonProps {
    control: Control<TestingControlFormSchema>;
    onClick: () => void;
    loading: boolean;
    disabled: boolean;
}

function BulkSubmitButton({
    control,
    onClick,
    loading,
    disabled,
}: BulkSubmitButtonProps) {
    const { t } = useTranslation();
    const samplingTagIds = useWatch({ control, name: "samplingTagIds" });
    const selectedControlIds = useWatch({
        control,
        name: "selectedControlIds",
    });
    const { data: controlsData, status } = useControlsQuery();

    const selectedControls = controlsData?.content.filter((control) =>
        selectedControlIds?.includes(control.resource.id)
    );
    const { data: userGroupTagData } = useUserGroupsQuery();

    const totalTests = selectedControls
        ?.map((control) => control.resource)
        .reduce(
            (acc, control) =>
                acc +
                formatBulkCreatedTestsToNumber(
                    control,
                    samplingTagIds,
                    userGroupTagData ?? []
                ),
            0
        );

    return (
        <Button
            size="large"
            variant="primary"
            disabled={disabled || status !== "success"}
            loading={loading || status === "pending"}
            onClick={onClick}
        >
            {!totalTests
                ? t("TestingControlView_CreateButton")
                : t("TestingControlView_CreateBulkButton", {
                      count: totalTests,
                  })}
        </Button>
    );
}
